<template>
    <div class="map-container">
        <div
            :id="mapContainerId"
            class="mapbox-component" />

        <div
            v-if="download"
            class="position-absolute top-0 right-0 m-2">
            <a
                href="#"
                target="_blank"
                class="text-white"
                title="Karte als Bild herunterladen"
                @click.prevent="downloadMap">

                <font-awesome-layers
                    class="fa-fixed-width fa-2x">
                    <font-awesome-icon icon="expand" />
                    <font-awesome-icon
                        icon="camera"
                        transform="shrink-9" />
                </font-awesome-layers>
            </a>
        </div>
    </div>
</template>

<script>

import baseMapMixin from "@core/mixins/baseMapMixin";
import 'mapbox-gl/dist/mapbox-gl.css'
import {v4 as uuidv4} from 'uuid'

export default {
    name: "BaseMap",
    mixins: [baseMapMixin],
    props: {
        eventId: { // We need to know for which event the map is used so we can update the api accesses in the database
            type: [String, Number]
        },
    },
    data() {
        return {
            token: useRuntimeConfig().public?.mapboxToken, // mapbox access token
            style: useRuntimeConfig().public?.mapboxStyle,
            map: null,
            mapIsLoaded: false,
            standardZoom: 5
        }
    },
    computed: {
        mapContainerId() {
            return this.uid + ':mapbox-component'
        }
    },
    methods: {
        async createMap() {
            const mapboxgl = await this.loadMapbox()
            mapboxgl.accessToken = this.token

            this.map = new mapboxgl.Map({
                container: this.mapContainerId,
                style: this.style,
                center: this.defaultMapPosition,
                zoom: this.standardZoom,
                preserveDrawingBuffer: true
            })

            this.map.on('load', () => {
                this.addMarkerLayers()
                this.addClusterLayers()
            })

            this.map.addControl(new mapboxgl.ScaleControl({
                maxWidth: 80,
                unit: 'metric'
            }), 'bottom-left');

            // Store Mapbox access in database
            await this.$axios.post(this.$apiRoute('api.registration.mapboxApiAccess.registerAccess'), {eventId: this.eventId})
            this.mapIsLoaded = true
        },
    },
    mounted() {
        this.createMap()
    },
    created() {
        this.uid = uuidv4()
    },
    beforeUnmount() {
        this.$nextTick(() => {
            this.stopLayerAnimations()
            if (this.map) this.map.remove();
        });
    },
    watch: {
        clusterSource: {
            handler() {
                if(this.map) {
                    const geojsonSource = this.map.getSource('events');
                    geojsonSource?.setData(this.clusterSource);
                }
                if (this.clusterSource?.features?.length) {
                    this.adjustMapBoundsToFitAllPoints(this.clusterSource.features.map(feature => feature.geometry.coordinates))
                }
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
.map-container{
    height: 100%;
    width: 100%;
}
.mapbox-component {
    position: relative;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
}
::v-deep .mapboxgl-ctrl-attrib-button {
    display: none;
}
::v-deep .mapboxgl-map {
    font: inherit;
}
::v-deep .popup-address {
    white-space: pre-line;
}
::v-deep .popup-image {
    object-fit: cover;
    width: 100%;
    height: 100%;
}
::v-deep .popup-image-container {
    position: relative;
    max-height: 12rem;
    overflow: hidden;
}
::v-deep .image-licence {
    position: absolute;
    bottom: 0;
    width: 100%;
    background: $gray-900;
    color: $white;
    padding: .25rem;
    opacity: .7;
    text-overflow: ellipsis;
    font-size: .625rem;
    overflow: hidden
}
</style>
